import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data.user);
      setIsAuthenticated(true);
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(msg || "Login failed. Please check your credentials.");
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <div>
            <div className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900" data-testid="login-hero-title">
              Geo-tagged video proof.
              <span className="block text-slate-600">Disputes end here.</span>
            </div>
            <div className="mt-4 text-base md:text-lg text-slate-600" data-testid="login-hero-subtitle">
              Capture loading/unloading with watermarked video + photos and a clear audit trail for admins.
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white/80 p-6" data-testid="login-hero-card">
              <div className="text-sm text-slate-700" data-testid="login-hero-points-title">MVP highlights</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600" data-testid="login-hero-points">
                <li>• Mandatory GPS gating before capture</li>
                <li>• Watermark on every frame + every photo</li>
                <li>• Manual counters (Cases/Sacks) with summary</li>
                <li>• Admin can create users & view all sessions</li>
              </ul>
            </div>
          </div>

          <Card data-testid="login-card" className="border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle data-testid="login-card-title">Sign in</CardTitle>
              <CardDescription data-testid="login-card-desc">Use Google or your assigned email/password.</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="email" className="w-full" data-testid="login-method-tabs">
                <TabsList
                  className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 border border-slate-200 p-1"
                  data-testid="login-method-tabs-list"
                >
                  <TabsTrigger
                    value="email"
                    data-testid="login-tab-email"
                    className="rounded-xl text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                  >
                    Email
                  </TabsTrigger>
                  <TabsTrigger
                    value="google"
                    data-testid="login-tab-google"
                    className="rounded-xl text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                  >
                    Google
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email" data-testid="login-email-tab-content">
                  <form onSubmit={onEmailLogin} className="mt-4 space-y-4" data-testid="login-email-form">
                    <div className="space-y-2">
                      <Label htmlFor="email" data-testid="login-email-label">Email</Label>
                      <Input
                        data-testid="login-email-input"
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        className="pod-input-light !bg-white !border-slate-300 !shadow-sm focus-visible:!ring-slate-400"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password" data-testid="login-password-label">Password</Label>
                      <Input
                        data-testid="login-password-input"
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pod-input-light !bg-white !border-slate-300 !shadow-sm focus-visible:!ring-slate-400"
                        required
                      />
                    </div>

                    {error ? (
                      <div data-testid="login-error-message" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                        {error}
                      </div>
                    ) : null}

                    <Button
                      data-testid="login-form-submit-button"
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800"
                    >
                      {loading ? "Signing in…" : "Sign in"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="google" data-testid="login-google-tab-content">
                  <div className="mt-4 space-y-3">
                    <Button
                      data-testid="login-google-button"
                      type="button"
                      className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800"
                      onClick={googleLogin}
                    >
                      Continue with Google
                    </Button>
                    <div className="text-xs text-slate-600" data-testid="login-google-note">
                      Google login creates/updates your profile automatically.
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
